import React, { useEffect, useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, Trophy, Swords, Crown, Sparkles, BookOpen, Package, Heart, Coins, Recycle, Flame, Loader2 } from "lucide-react";
import axios from "axios";
import { t } from "@/utils/i18n";
import useTabMusic from "@/hooks/useTabMusic";

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

function Stat({ icon: Icon, label, value, color = "#cfb53b", testid }) {
  return (
    <div className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-3 sm:p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <span className="text-[10px] sm:text-xs text-[#94a3b8] uppercase font-heading tracking-wide">{label}</span>
      </div>
      <p className="font-heading text-lg sm:text-2xl text-[#e2e8f0]">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { game } = useGame();
  const { token, API } = useAuth();
  useTabMusic('stats');  // Iter 104 — per-section music
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!game?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/game/${game.id}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [game?.id, API, token]);

  if (!game) return null;
  const lang = game.language || "en";
  const isIt = lang.startsWith("it");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-loading">
        <Loader2 size={28} className="animate-spin text-[#cfb53b]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-12 text-red-400" data-testid="dashboard-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const character = data.character;
  const story = data.story;
  const combat = data.combat;
  const collection = data.collection;
  const bestiary = data.bestiary;

  const rarityEntries = Object.entries(collection.rarity_distribution || {});
  const totalCards = collection.total_cards || 0;

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="dashboard-view">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <BarChart3 size={18} />
            {isIt ? "Dashboard Avventuriero" : "Adventurer Dashboard"}
          </h2>
        </div>

        {/* Character Panel */}
        <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-4 sm:p-5 mb-4">
          <h3 className="font-heading text-sm sm:text-base text-[#cfb53b] mb-3">
            {character.name} · <span className="capitalize">{character.class_type}</span>
            {character.evolution_count > 0 && (
              <span className="ml-2 text-[10px] text-purple-400">
                {isIt ? "Evoluzioni" : "Evolutions"}: {character.evolution_count}
              </span>
            )}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Stat icon={Sparkles} label={t(lang, 'level')} value={character.level} testid="stat-level" />
            <Stat icon={Heart} label={t(lang, 'hp')} value={`${character.hp}/${character.max_hp}`} color="#ef4444" testid="stat-hp" />
            <Stat icon={Coins} label={isIt ? "Oro" : "Gold"} value={character.gold} color="#cfb53b" testid="stat-gold" />
            <Stat icon={Recycle} label={isIt ? "Polvere" : "Dust"} value={character.dust || 0} color="#a855f7" testid="stat-dust" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Story */}
          <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4">
            <h3 className="font-heading text-sm text-[#cfb53b] mb-3 flex items-center gap-2">
              <BookOpen size={14} /> {isIt ? "Narrazione" : "Story"}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <Stat icon={BookOpen} label={isIt ? "Pagine" : "Pages"} value={story.pages_generated} testid="stat-pages" />
              <Stat icon={Crown} label={isIt ? "Capitolo" : "Chapter"} value={story.current_chapter} testid="stat-chapter" />
              <Stat icon={Flame} label={isIt ? "Scene" : "Scenes"} value={`${story.scenes_in_chapter}/${story.chapter_target}`} testid="stat-scenes" />
            </div>
          </div>

          {/* Combat */}
          <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4">
            <h3 className="font-heading text-sm text-[#cfb53b] mb-3 flex items-center gap-2">
              <Swords size={14} /> {isIt ? "Combattimento" : "Combat"}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <Stat icon={Trophy} label={isIt ? "Vittorie" : "Wins"} value={combat.battles_won} color="#10b981" testid="stat-wins" />
              <Stat icon={Crown} label={isIt ? "Boss" : "Bosses"} value={combat.bosses_defeated} color="#f59e0b" testid="stat-bosses" />
              <Stat icon={Sparkles} label={isIt ? "Combo" : "Combos"} value={combat.combos_triggered} color="#a855f7" testid="stat-combos" />
            </div>
          </div>
        </div>

        {/* Collection */}
        <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4 mb-4">
          <h3 className="font-heading text-sm text-[#cfb53b] mb-3 flex items-center gap-2">
            <Package size={14} /> {isIt ? "Collezione" : "Collection"}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Stat icon={Package} label={isIt ? "Carte totali" : "Total cards"} value={totalCards} testid="stat-total-cards" />
            <Stat icon={Sparkles} label={isIt ? "Foil" : "Foil"} value={collection.foil_count} color="#fef08a" testid="stat-foil" />
            <Stat icon={BookOpen} label={isIt ? "Arte IA" : "AI art"} value={collection.art_generated || 0} color="#3b82f6" testid="stat-art" />
            <Stat icon={Trophy} label={isIt ? "Bestiario" : "Bestiary"} value={`${bestiary.defeated}/${bestiary.total_entries}`} color="#10b981" testid="stat-bestiary" />
          </div>

          {/* Rarity bars */}
          {totalCards > 0 && (
            <div className="space-y-1.5" data-testid="dashboard-rarity-bars">
              <p className="text-[10px] text-[#94a3b8] uppercase font-heading tracking-wide mb-1">
                {isIt ? "Distribuzione rarità" : "Rarity distribution"}
              </p>
              {["common", "uncommon", "rare", "epic", "legendary"].map(r => {
                const count = collection.rarity_distribution[r] || 0;
                if (count === 0) return null;
                const pct = Math.round((count / totalCards) * 100);
                return (
                  <div key={r} className="flex items-center gap-2">
                    <span className="text-[10px] font-heading uppercase w-20 shrink-0" style={{ color: RARITY_COLOR[r] }}>
                      {t(lang, `rarity_${r}`)}
                    </span>
                    <div className="flex-1 h-2 bg-[#0a0a0c] rounded overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, background: RARITY_COLOR[r] }}
                      />
                    </div>
                    <span className="text-[10px] text-[#94a3b8] w-12 text-right">{count} ({pct}%)</span>
                  </div>
                );
              })}
              {rarityEntries.length === 0 && (
                <p className="text-xs text-[#64748b] italic">—</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
